import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// ── Fixed assets ────────────────────────────────────────────────────────────────────────────────
// Two rules meet on this screen and they are not the same one. Revenue Code s.65 bis (2) says how
// much depreciation is DEDUCTIBLE — only at the decree's rates, prorated by the days the asset was
// held. DBD 2544 ข้อ 3.3(4) says the books must show each asset's increase and decrease, whether or
// not anything is deductible. So the rate is capped by the class, and the register prints movement.
//
// The maximum rates are reference data carried with their source, because they were transcribed from
// a summary of the decree rather than from the decree itself. The screen says so.

type Cls = { code: string; name_th: string; name_en: string; max_rate: number | null; depreciable: number; source: string; note: string };
type Asset = { id: string; asset_no: string; name_th: string; name_en: string; class_th: string; class_en: string;
  acquired_date: string; cost: number; salvage: number; rate: number; accumulated: number; nbv: number;
  disposed_date: string; depreciable: number };
type Row = { assetId: string; assetNo: string; name: string; days: number; amount: number; chargedThisPeriod: number; skipped: string };
type Account = { id: string; code: string; name_th: string; name_en: string; class: string };

const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const baht = (s: number) => (Number(s || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const satang = (v: string) => Math.round((parseFloat(String(v).replace(/,/g, '')) || 0) * 100);

const btn: React.CSSProperties = {
  padding: '6px 11px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};

export default function FixedAssets({ clientId, periodId, periodClosed, onChanged }:
  { clientId: string; periodId: string; periodClosed: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const [classes, setClasses] = useState<Cls[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const [f, setF] = useState({ classCode: 'other', nameTh: '', nameEn: '', acquiredDate: '', cost: '', salvage: '', rate: '',
    assetAccountId: '', accumAccountId: '', expenseAccountId: '' });

  const load = useCallback(async () => {
    try {
      const [a, d]: any[] = await Promise.all([api.faAssets(clientId), api.faDepreciation(clientId, periodId)]);
      setAssets(a.assets || []); setRows(d.rows || []);
    } catch (e: any) { setErr(e.message); setAssets([]); setRows([]); }
  }, [clientId, periodId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.faClasses().then((d: any) => setClasses(d.classes || [])).catch(() => setClasses([]));
    api.jnlAccounts(clientId).then((d: any) => {
      const list: Account[] = d.accounts || [];
      setAccounts(list);
      // Sensible starting points from the house chart, still changeable: two buildings can
      // legitimately sit in different accounts, so the posting must never guess silently.
      setF(x => ({ ...x,
        assetAccountId: x.assetAccountId || (list.find(a => a.code === '1500')?.id || ''),
        accumAccountId: x.accumAccountId || (list.find(a => a.code === '1590')?.id || ''),
        expenseAccountId: x.expenseAccountId || (list.find(a => a.code === '5600')?.id || '') }));
    }).catch(() => setAccounts([]));
  }, [clientId]);

  const cls = classes.find(c => c.code === f.classCode);
  const due = (rows || []).filter(r => !r.skipped && r.amount > 0 && !r.chargedThisPeriod);
  const dueTotal = due.reduce((s, r) => s + r.amount, 0);

  const save = async () => {
    setBusy('save'); setErr(''); setMsg('');
    try {
      const r: any = await api.faRegister(clientId, { ...f, cost: satang(f.cost), salvage: satang(f.salvage), rate: f.rate ? Number(f.rate) : undefined });
      setMsg(t('pa_assets_registered_msg', { no: r.assetNo, rate: r.rate }));
      setF(x => ({ ...x, nameTh: '', nameEn: '', acquiredDate: '', cost: '', salvage: '', rate: '' }));
      setAdding(false); await load();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const postDep = async () => {
    setBusy('dep'); setErr(''); setMsg('');
    try {
      const r: any = await api.faPostDepreciation(clientId, periodId);
      setMsg(r.assets ? t('pa_assets_dep_draft', { n: r.assets, amount: baht(r.amount) }) : t('pa_assets_dep_none'));
      await load(); onChanged();
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const openRegister = async () => {
    setBusy('reg'); setErr('');
    try {
      const url = await api.faRegisterUrl(periodId);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  if (assets === null) return null;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className="ti ti-building-warehouse" style={{ fontSize: 18, color: 'var(--accent)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pa_assets_register_name')} · {t('pa_assets_title')}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>
            {assets.length ? t('pa_assets_summary', { n: assets.length, nbv: baht(assets.reduce((s, a) => s + a.nbv, 0)) }) : t('pa_assets_empty')}
            {dueTotal ? ` · ${t('pa_assets_due', { amount: baht(dueTotal) })}` : ''}
          </span>
        </span>
        {!!assets.length && <button onClick={openRegister} disabled={!!busy} style={btn}>
          <i className={busy === 'reg' ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> {t('pa_assets_open_register')}</button>}
        {!periodClosed && !!dueTotal && (
          <button onClick={postDep} disabled={!!busy} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>
            <i className={busy === 'dep' ? 'ti ti-loader' : 'ti ti-calculator'} aria-hidden /> {t('pa_assets_charge_dep', { amount: baht(dueTotal) })}
          </button>
        )}
        {!periodClosed && <button onClick={() => setAdding(a => !a)} style={{ ...btn, background: adding ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
          <i className="ti ti-plus" aria-hidden /> {t('pa_assets_add')}</button>}
        {!!assets.length && <button onClick={() => setOpen(o => !o)} style={btn}>{open ? t('pa_assets_hide') : t('pa_assets_open')}</button>}
      </div>

      {msg && <div style={{ fontSize: 12.5, color: '#166534', background: '#E8F6EE', borderTop: '1px solid #BBE5CB', padding: '8px 13px' }}>{msg}</div>}
      {err && <div style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>{err}</div>}

      {adding && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '11px 13px 13px' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_class')}<br />
              <select value={f.classCode} onChange={e => setF({ ...f, classCode: e.target.value, rate: '' })} style={{ ...inp, width: 250 }}>
                {classes.map(c => <option key={c.code} value={c.code}>{c.name_th} — {c.name_en}{c.max_rate != null ? ` (≤${c.max_rate}%)` : ` (${t('pa_assets_no_dep')})`}</option>)}
              </select></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_acquired')}<br />
              <input type="date" value={f.acquiredDate} onChange={e => setF({ ...f, acquiredDate: e.target.value })} style={{ ...inp, width: 150 }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_cost')}<br />
              <input value={f.cost} onChange={e => setF({ ...f, cost: e.target.value })} inputMode="decimal" style={{ ...inp, width: 130, textAlign: 'right' }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_salvage')}<br />
              <input value={f.salvage} onChange={e => setF({ ...f, salvage: e.target.value })} inputMode="decimal" style={{ ...inp, width: 120, textAlign: 'right' }} /></label>
            <label style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t('pa_assets_f_rate')} {cls?.max_rate != null && <span style={{ color: 'var(--text-tertiary)' }}>(≤{cls.max_rate})</span>}<br />
              <input value={f.rate} onChange={e => setF({ ...f, rate: e.target.value })} placeholder={cls?.max_rate != null ? String(cls.max_rate) : '—'}
                disabled={!cls?.depreciable} inputMode="decimal" style={{ ...inp, width: 90, textAlign: 'right' }} /></label>
          </div>
          <input value={f.nameTh} onChange={e => setF({ ...f, nameTh: e.target.value })} placeholder={t('pa_assets_f_name_th')} style={{ ...inp, width: '100%', marginBottom: 6 }} />
          <input value={f.nameEn} onChange={e => setF({ ...f, nameEn: e.target.value })} placeholder={t('pa_assets_f_name_en')} style={{ ...inp, width: '100%', marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 9 }}>
            {([['assetAccountId', 'pa_assets_f_acct_asset'], ['accumAccountId', 'pa_assets_f_acct_accum'], ['expenseAccountId', 'pa_assets_f_acct_expense']] as const).map(([k, label]) => (
              <label key={k} style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>{t(label)}<br />
                <select value={(f as any)[k]} onChange={e => setF({ ...f, [k]: e.target.value })} style={{ ...inp, width: 250 }}>
                  <option value="">—</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.code} · {a.name_th}</option>)}
                </select></label>
            ))}
          </div>
          {cls && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8, lineHeight: 1.6 }}>
            {cls.note && <div>{cls.note}</div>}
            <div>{t('pa_assets_max_rate_source', { source: cls.source })}</div>
          </div>}
          <div style={{ display: 'flex', gap: 7, justifyContent: 'flex-end' }}>
            <button onClick={() => setAdding(false)} style={btn}>{t('pa_assets_cancel')}</button>
            <button onClick={save} disabled={!!busy} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>
              <i className={busy === 'save' ? 'ti ti-loader' : 'ti ti-device-floppy'} aria-hidden /> {t('pa_assets_submit')}
            </button>
          </div>
        </div>
      )}

      {open && !!assets.length && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 13px 13px', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
              <th style={{ textAlign: 'left', padding: '0 6px 5px 0' }}>{t('pa_assets_th_no')}</th>
              <th style={{ textAlign: 'left', padding: '0 6px 5px' }}>{t('pa_assets_th_item')}</th>
              <th style={{ textAlign: 'left', padding: '0 6px 5px' }}>{t('pa_assets_th_acquired')}</th>
              <th style={{ textAlign: 'right', padding: '0 6px 5px' }}>{t('pa_assets_th_cost')}</th>
              <th style={{ textAlign: 'right', padding: '0 6px 5px' }}>{t('pa_assets_th_accum')}</th>
              <th style={{ textAlign: 'right', padding: '0 6px 5px' }}>{t('pa_assets_th_nbv')}</th>
              <th style={{ textAlign: 'right', padding: '0 0 5px 6px' }}>{t('pa_assets_th_year')}</th>
            </tr></thead>
            <tbody>
              {assets.map(a => {
                const r = (rows || []).find(x => x.assetId === a.id);
                return (
                  <tr key={a.id} style={{ borderTop: '1px solid var(--border)', opacity: a.disposed_date ? 0.55 : 1 }}>
                    <td style={{ padding: '6px 6px 6px 0', whiteSpace: 'nowrap', fontWeight: 700 }}>{a.asset_no}</td>
                    <td style={{ padding: '6px' }}>
                      {a.name_th}<span style={{ color: 'var(--text-tertiary)' }}> {a.name_en}</span><br />
                      <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>
                        {a.class_th} · {Number(a.depreciable) ? `${a.rate}%` : t('pa_assets_no_dep')}
                        {a.disposed_date ? ` · ${t('pa_assets_disposed_on', { date: dmy(a.disposed_date) })}` : ''}
                      </span>
                    </td>
                    <td style={{ padding: '6px', whiteSpace: 'nowrap', color: 'var(--text-tertiary)' }}>{dmy(a.acquired_date)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{baht(a.cost)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)' }}>{baht(a.accumulated)}</td>
                    <td style={{ padding: '6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{baht(a.nbv)}</td>
                    <td style={{ padding: '6px 0 6px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {/* What this period charges, and whether it has already been recorded — the two
                          are different questions and the screen answers both. */}
                      {r?.chargedThisPeriod ? <span style={{ color: '#166534' }}>{baht(r.chargedThisPeriod)} ✓</span>
                        : r?.skipped ? <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{r.skipped}</span>
                        : r?.amount ? <span>{baht(r.amount)}<span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}> · {t('pa_assets_days', { n: r.days })}</span></span>
                        : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
